import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, DollarSign, Shield, UserCheck, Building2, Handshake, Compass, GraduationCap, Globe, Heart, Crown, Star } from 'lucide-react';
import NotificationBell from '@/components/NotificationBell';
import UserDropdownMenu from '@/components/UserDropdownMenu';

// Premium Access Badge - Shows days remaining for org-granted premium access
const PremiumAccessBadge = ({ user }) => {
  const orgPremiumAccess = user?.org_premium_access;
  
  if (!orgPremiumAccess?.enabled) {
    return null;
  }
  
  const expiresAt = orgPremiumAccess.expires_at ? new Date(orgPremiumAccess.expires_at) : null;
  const now = new Date();
  const daysRemaining = expiresAt 
    ? Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)))
    : 0;
  
  if (daysRemaining <= 0) {
    return null;
  }
  
  const isUrgent = daysRemaining <= 7;
  const isModerate = daysRemaining <= 30 && daysRemaining > 7;
  
  return (
    <div 
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isUrgent 
          ? 'bg-red-100 text-red-700' 
          : isModerate 
            ? 'bg-amber-100 text-amber-700'
            : 'bg-emerald-100 text-emerald-700'
      }`}
      title={`Premium Access: ${daysRemaining} days remaining via ${orgPremiumAccess.org_name || 'Organization'}`}
    >
      <Crown className="h-3 w-3" />
      <span>{daysRemaining}d</span>
    </div>
  );
};

const DashboardNav = ({ user, hasRole, isAdmin, hasOrgMembership, onLogout, onPersonalizePath }) => {
  const navigate = useNavigate();
  
  // Super admin check - only super_admin sees admin tools
  const isSuperAdmin = user?.roles?.includes('super_admin');
  
  // Partner role checks
  const isOrganizationPartner = user?.roles?.includes('organization_partner');
  const isMarketplacePartner = user?.roles?.includes('marketplace_partner');
  const isAffiliatePartner = user?.roles?.includes('affiliate_partner');
  const isStrategicPartner = user?.roles?.includes('strategic_partner');
  const isClinicalSupportPartner = user?.roles?.includes('clinical_support_partner');
  
  // Strategic partners get access to ALL partner dashboards
  const hasCoachAccess = isMarketplacePartner || isStrategicPartner;
  const hasAffiliateAccess = isAffiliatePartner || isStrategicPartner;
  const hasOrganizationAccess = isOrganizationPartner || isStrategicPartner;
  const hasClinicalSupportAccess = isClinicalSupportPartner;
  
  // Check if user has any partner role
  const hasAnyPartnerRole = isOrganizationPartner || isMarketplacePartner || isAffiliatePartner || isStrategicPartner || isClinicalSupportPartner;
  
  // GED Instructor/Coach/Admin check
  const isGEDInstructor = user?.roles?.some(role => ['ged_instructor', 'ged_coach', 'ged_admin'].includes(role));

  // Build navigation items based on user roles
  const navItems = [];
  
  // Coach Dashboard - for Marketplace Partners, Strategic Partners, or SUPER ADMIN
  if (isSuperAdmin || hasCoachAccess) {
    navItems.push({
      id: 'coach',
      label: 'Coach Dashboard',
      icon: UserCheck,
      onClick: () => navigate('/coach'),
      className: 'hover:bg-blue-50 hover:text-blue-600',
      testId: 'coach-dashboard-btn'
    });
  }
  
  // GED Instructor Dashboard
  if (isSuperAdmin || isGEDInstructor) {
    navItems.push({
      id: 'ged-instructor',
      label: 'GED Instructor Dashboard',
      icon: GraduationCap,
      onClick: () => navigate('/ged-instructor-dashboard'),
      className: 'hover:bg-emerald-50 hover:text-emerald-600',
      testId: 'ged-instructor-dashboard-btn'
    });
  }
  
  // Affiliate Partner Dashboard
  if (isSuperAdmin || hasAffiliateAccess) {
    navItems.push({
      id: 'affiliate',
      label: 'Affiliate Dashboard',
      icon: DollarSign,
      onClick: () => navigate('/affiliate-dashboard'),
      className: 'hover:bg-green-50 hover:text-green-600',
      testId: 'affiliate-dashboard-btn'
    });
  }
  
  // Clinical Support Dashboard
  if (isSuperAdmin || hasClinicalSupportAccess) {
    navItems.push({
      id: 'clinical-support',
      label: 'Clinical Support Dashboard',
      icon: Heart,
      onClick: () => navigate('/clinical-support-dashboard'),
      className: 'hover:bg-pink-50 hover:text-pink-600',
      testId: 'clinical-support-dashboard-btn'
    });
  }
  
  // My Coaches - for all users
  navItems.push({
    id: 'my-coaches',
    label: 'My Coaches',
    icon: UserCheck,
    onClick: () => navigate('/my-coach'),
    className: 'hover:bg-green-50 hover:text-green-600'
  });
  
  // My Organization - only for users in an organization
  if (hasOrgMembership) {
    navItems.push({
      id: 'my-organization',
      label: 'My Organization',
      icon: Building2,
      onClick: () => navigate('/my-organization'),
      className: 'hover:bg-sky-50 hover:text-sky-600'
    });
  }
  
  // Partnership Dashboard
  if (isSuperAdmin || hasAnyPartnerRole) {
    navItems.push({
      id: 'partnership',
      label: 'Partnership Dashboard',
      icon: Handshake,
      onClick: () => navigate('/partner-dashboard'),
      className: 'hover:bg-purple-50 hover:text-purple-600'
    });
  }
  
  // Organization Dashboard
  if (isSuperAdmin || hasOrganizationAccess) {
    navItems.push({
      id: 'org-dashboard',
      label: 'Organization Dashboard',
      icon: Building2,
      onClick: () => navigate('/organization-dashboard'),
      className: 'hover:bg-indigo-50 hover:text-indigo-600',
      testId: 'organization-dashboard-btn'
    });
  }
  
  // Personalize My Path - for all users
  if (onPersonalizePath) {
    navItems.push({
      id: 'personalize',
      label: 'Personalize My Path',
      icon: Compass,
      onClick: onPersonalizePath,
      className: 'hover:bg-indigo-50 hover:text-indigo-600'
    });
  }
  
  // Admin - SUPER ADMIN ONLY
  if (isSuperAdmin) {
    navItems.push({
      id: 'admin',
      label: 'Admin',
      icon: Shield,
      onClick: () => navigate('/admin'),
      className: 'hover:bg-purple-50 hover:text-purple-600',
      testId: 'admin-management-btn'
    });
  }

  // Split items into rows (max 6 items per row for better alignment)
  const MAX_ITEMS_PER_ROW = 6;
  const firstRowItems = navItems.slice(0, MAX_ITEMS_PER_ROW);
  const secondRowItems = navItems.slice(MAX_ITEMS_PER_ROW);

  return (
    <>
      {/* Mobile Navigation - Only visible on mobile/tablet */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 py-2 safe-area-inset">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_role-expiry-dash/artifacts/swo6wssk_career_support_80px_transparent_EXACT.png" 
              alt="Career Support" 
              className="h-12 w-auto"
            />
          </div>
          <div className="flex items-center gap-2">
            <PremiumAccessBadge user={user} />
            <NotificationBell />
            <UserDropdownMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>
      
      {/* Desktop Navigation - Dashboard Tools Row (NO user menu - handled by LoggedInHeader above) */}
      <nav aria-label="Dashboard navigation" className="hidden lg:block px-6 py-2 bg-gray-50 border-b border-gray-200 relative z-[90]" style={{ overflow: 'visible' }}>
        <div className="max-w-7xl mx-auto">
          {/* Dashboard Tools Row */}
          <div className="flex items-center gap-1 flex-wrap">
            {firstRowItems.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                onClick={item.onClick}
                className={`justify-start whitespace-nowrap ${item.className}`}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                {item.label}
              </Button>
            ))}
            {secondRowItems.map((item) => (
              <Button
                key={item.id}
                variant="ghost"
                size="sm"
                onClick={item.onClick}
                className={`justify-start whitespace-nowrap ${item.className}`}
                data-testid={item.testId}
              >
                <item.icon className="w-4 h-4 mr-2 flex-shrink-0" />
                {item.label}
              </Button>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
};

export default DashboardNav;
